import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchSupportSummary } from '../api/endpoints';
import type { SupportSummary } from '../api/types';

const CHANGED_EVENT = 'support-summary-changed';

/**
 * Tells the sidebar badges to re-read. Called by the request page once it has loaded (opening a
 * request marks it read, which the navigation itself is too early to see) and after a reply or a
 * status change.
 */
export function notifySupportSummaryChanged() {
  window.dispatchEvent(new Event(CHANGED_EVENT));
}

/**
 * The support badge counts for the sidebar. Re-read on every navigation and whenever
 * notifySupportSummaryChanged is called; there is no polling. A failure just leaves the badge off.
 */
export function useSupportSummary(enabled: boolean): SupportSummary | null {
  const { pathname } = useLocation();
  const [summary, setSummary] = useState<SupportSummary | null>(null);
  const [changes, setChanges] = useState(0);

  useEffect(() => {
    const onChanged = () => setChanges((n) => n + 1);
    window.addEventListener(CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(CHANGED_EVENT, onChanged);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    fetchSupportSummary()
      .then((s) => {
        if (!cancelled) setSummary(s);
      })
      .catch(() => {
        if (!cancelled) setSummary(null);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, pathname, changes]);

  return summary;
}
